/**
 * User takeover recording — captures and summarizes user interactions
 * during headed browser sessions controlled by an AI agent.
 *
 * Two modes:
 * 1. **Always-on**: Between agent tool calls, user clicks/typing are captured
 *    and summarized in the next tool result.
 * 2. **Pause mode**: User clicks the badge to pause the agent. During pause,
 *    events are enriched with element info. On resume, a snapshot is taken
 *    and the full summary is prepended to the next tool result.
 *
 * @module session/user-takeover
 */

import { captureElementAtPoint, consoleLogger } from '@chatsdet/browser-core';
import type { PlaywrightClient, BrowserToolExecutor } from '@chatsdet/browser-core';
import { INTERACTION_CAPTURE_SCRIPT } from '../record-interaction-script.js';
import type { TakeoverEvent } from './types.js';

/* ── Constants ─────────────────────────────────────────────────────── */

/** How often the always-on poll drains user input from a headed page (ms). */
const ACTIVITY_POLL_INTERVAL_MS = 500;
/**
 * The agent is deferred while the user has produced input within this window.
 * Once the user is idle for longer, the agent auto-resumes. Kept in loose sync
 * with the badge's DRIVING_FLASH_MS so the on-screen indicator matches behavior.
 */
export const RESUME_IDLE_MS = 2_500;
/** Cap on buffered pending user events to bound memory during long takeovers. */
const MAX_PENDING_EVENTS = 500;
const EVALUATE_TIMEOUT_MS = 3_000;
const MAX_STRING_LENGTH = 200;

/* ── Session interface ─────────────────────────────────────────────── */

/** Minimal session interface for user-takeover helpers. */
export interface TakeoverSession {
  id: string;
  mode: 'headed' | 'headless';
  client: PlaywrightClient;
  executor: BrowserToolExecutor;
  /**
   * Epoch-ms of the last observed user input (click/keydown/typing/nav), or
   * null if the user has never touched this session. Control is derived from
   * this: the agent is deferred while `now - lastUserInputAt < RESUME_IDLE_MS`
   * ({@link isUserDriving}) and auto-resumes once the user goes idle.
   */
  lastUserInputAt: number | null;
  /** User events observed since the last time they were handed to the agent. */
  pendingUserEvents: TakeoverEvent[];
  /** Always-on poll timer that drains + enriches user input on headed sessions. */
  activityPollTimer: ReturnType<typeof setInterval> | null;
  /**
   * In-flight activity poll drain. Tool calls wait for a poll that already owns
   * a page event batch so user input is not lost between the poll and hot path.
   */
  activityPollDrain: Promise<void> | null;
  /**
   * True while the agent's own Playwright action is executing. The activity
   * poll skips draining during this window so the agent's own synthetic events
   * are never misattributed as user input (they are discarded post-action).
   */
  toolInFlight: boolean;
}

/* ── Event validation & sanitization ───────────────────────────────── */

function truncate(s: unknown, maxLen = MAX_STRING_LENGTH): string | undefined {
  if (typeof s !== 'string') return undefined;
  return s.length > maxLen ? s.slice(0, maxLen) : s;
}

/**
 * Validate and sanitize a raw event object from the page.
 * The page is untrusted — a hostile page can write arbitrary data
 * to window.__canaryRecordedEvents.
 */
function sanitizeEvent(raw: unknown): TakeoverEvent | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const evt = raw as Record<string, unknown>;
  const ts = typeof evt.ts === 'number' ? evt.ts : Date.now();

  switch (evt.type) {
    case 'click':
      if (typeof evt.x !== 'number' || typeof evt.y !== 'number') return null;
      return {
        type: 'click',
        ts,
        x: evt.x,
        y: evt.y,
        tagName: truncate(evt.tagName, 50) ?? 'UNKNOWN',
        id: truncate(evt.id, 100),
        testId: truncate(evt.testId, 100),
        ariaLabel: truncate(evt.ariaLabel),
        role: truncate(evt.role, 50),
        textContent: truncate(evt.textContent),
        className: truncate(evt.className),
      };

    case 'input':
      return {
        type: 'input',
        ts,
        tagName: truncate(evt.tagName, 50) ?? 'INPUT',
        id: truncate(evt.id, 100),
        inputType: truncate(evt.inputType, 50),
        ariaLabel: truncate(evt.ariaLabel),
        value: truncate(evt.value) ?? '',
        isFinal: evt.isFinal === true,
      };

    case 'change':
      return {
        type: 'change',
        ts,
        tagName: truncate(evt.tagName, 50) ?? 'INPUT',
        id: truncate(evt.id, 100),
        inputType: truncate(evt.inputType, 50),
        ariaLabel: truncate(evt.ariaLabel),
        value: truncate(evt.value) ?? '',
      };

    case 'keydown':
      if (typeof evt.key !== 'string') return null;
      return { type: 'keydown', ts, key: evt.key.slice(0, 20) };

    case 'navigation':
      if (typeof evt.url !== 'string') return null;
      try {
        const parsed = new URL(evt.url);
        const safeUrl = `${parsed.origin}${parsed.pathname}`.slice(0, 2048);
        return {
          type: 'navigation',
          ts,
          url: safeUrl,
          navigationType: truncate(evt.navigationType, 50),
        };
      } catch {
        return null;
      }

    default:
      return null;
  }
}

/* ── Page interaction helpers ──────────────────────────────────────── */

/** Create a timeout promise that rejects after the given duration. */
function timeoutReject(ms = EVALUATE_TIMEOUT_MS): Promise<never> {
  return new Promise<never>((_, reject) =>
    setTimeout(() => reject(new Error('evaluate timed out')), ms)
  );
}

/** Discard any events currently in the page buffer (used after tool execution). */
export async function discardCapturedEvents(session: TakeoverSession): Promise<void> {
  if (session.mode !== 'headed') return;
  const page = await session.client.getPageForReplay();
  if (!page) return;
  try {
    await Promise.race([
      page.evaluate(() => {
        const w = window as unknown as {
          __canaryFlushAndDiscard?: () => void;
          __canaryRecordedEvents?: unknown[];
        };
        if (typeof w.__canaryFlushAndDiscard === 'function') {
          w.__canaryFlushAndDiscard();
        } else {
          w.__canaryRecordedEvents = [];
        }
      }),
      timeoutReject(),
    ]);
  } catch (err) {
    consoleLogger.debug?.(`[takeover:${session.id}] discardCapturedEvents failed`, {
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/** Drain events from the page, sanitize, and return. Shared by both drain paths. */
async function drainAndSanitize(session: TakeoverSession): Promise<TakeoverEvent[]> {
  const page = await session.client.getPageForReplay();
  if (!page) return [];

  const raw = await Promise.race([
    page.evaluate(() => {
      const w = window as unknown as { __canaryRecordedEvents?: unknown[] };
      const evts = w.__canaryRecordedEvents ?? [];
      w.__canaryRecordedEvents = [];
      return evts;
    }),
    timeoutReject(),
  ]);

  if (!Array.isArray(raw)) return [];
  const events: TakeoverEvent[] = [];
  for (const e of raw) {
    const sanitized = sanitizeEvent(e);
    if (sanitized) events.push(sanitized);
  }
  return events;
}

/* ── Activity-derived control (pure helpers) ───────────────────────── */

/**
 * Whether the user is currently "driving" — i.e. produced input within the
 * resume window. While driving, the agent's tool calls are deferred; once the
 * user is idle past {@link RESUME_IDLE_MS} the agent auto-resumes. Pure so it
 * can be unit-tested without a browser.
 */
export function isUserDriving(session: TakeoverSession, now: number = Date.now()): boolean {
  if (session.lastUserInputAt === null) return false;
  return now - session.lastUserInputAt < RESUME_IDLE_MS;
}

/**
 * Record observed user events: refresh the last-input clock (only when there is
 * genuine input) and append to the pending buffer, capped at
 * {@link MAX_PENDING_EVENTS}. Pure.
 */
export function recordUserEvents(
  session: TakeoverSession,
  events: TakeoverEvent[],
  now: number = Date.now()
): void {
  if (events.length === 0) return;
  session.lastUserInputAt = now;
  for (const evt of events) {
    if (session.pendingUserEvents.length >= MAX_PENDING_EVENTS) break;
    session.pendingUserEvents.push(evt);
  }
}

/** Return and clear the pending user events buffered since the last hand-off. Pure. */
export function takePendingUserEvents(session: TakeoverSession): TakeoverEvent[] {
  const events = session.pendingUserEvents;
  session.pendingUserEvents = [];
  return events;
}

/* ── Draining + enrichment (page I/O) ──────────────────────────────── */

/**
 * Drain the page's recorded user events, sanitize, and enrich click events that
 * lack identifying info (via captureElementAtPoint). Shared by the always-on
 * poll and the tool-call hot path's final pre-decision drain.
 */
export async function drainAndEnrichUserEvents(
  session: TakeoverSession
): Promise<TakeoverEvent[]> {
  if (session.mode !== 'headed') return [];
  let events: TakeoverEvent[];
  try {
    events = await drainAndSanitize(session);
  } catch (err) {
    consoleLogger.debug?.(`[takeover:${session.id}] drain failed`, {
      error: err instanceof Error ? err.message : String(err),
    });
    return [];
  }

  for (const evt of events) {
    if (evt.type === 'click' && !evt.ariaLabel && !evt.textContent) {
      try {
        const page = await session.client.getPageForReplay();
        if (page) {
          const info = await captureElementAtPoint(page, evt.x, evt.y);
          if (info) evt.elementInfo = info as unknown as Record<string, unknown>;
        }
      } catch (err) {
        consoleLogger.debug?.(`[takeover:${session.id}] captureElementAtPoint failed`, {
          error: err instanceof Error ? err.message : String(err),
          x: evt.x,
          y: evt.y,
        });
      }
    }
  }
  return events;
}

/* ── Always-on activity poll ───────────────────────────────────────── */

/**
 * Begin one activity poll drain if the session is eligible.
 *
 * Once started, this poll owns the page batch it drains. If a tool call starts
 * while the drain is awaiting page/enrichment work, the tool waits for this
 * promise before deciding whether to defer, so those already-drained user events
 * are recorded rather than dropped.
 */
export function beginActivityPollDrain(session: TakeoverSession): Promise<void> | null {
  if (session.mode !== 'headed') return null;
  if (session.toolInFlight) return null;
  if (session.activityPollDrain) return session.activityPollDrain;
  if (session.pendingUserEvents.length >= MAX_PENDING_EVENTS) return null;

  const drain = (async () => {
    try {
      const events = await drainAndEnrichUserEvents(session);
      recordUserEvents(session, events);
    } catch (err) {
      consoleLogger.debug?.(`[takeover:${session.id}] activity poll error`, {
        error: err instanceof Error ? err.message : String(err),
      });
    }
  })();

  session.activityPollDrain = drain;
  void drain.finally(() => {
    if (session.activityPollDrain === drain) {
      session.activityPollDrain = null;
    }
  });
  return drain;
}

/** Wait for a poll that already started before the current tool call. */
export async function waitForActivityPollDrain(session: TakeoverSession): Promise<void> {
  const drain = session.activityPollDrain;
  if (!drain) return;
  await drain;
}

/**
 * Start the always-on poll that continuously drains + enriches user input on a
 * headed session, so the user's actions are captured "as they happen" and fed
 * into the agent's next tool result. The poll skips starting while the agent's
 * own action is in flight ({@link TakeoverSession.toolInFlight}) so synthetic
 * events are never misattributed as user input.
 */
export function startActivityTracking(session: TakeoverSession): void {
  if (session.mode !== 'headed' || session.activityPollTimer) return;

  session.activityPollTimer = setInterval(() => {
    void beginActivityPollDrain(session);
  }, ACTIVITY_POLL_INTERVAL_MS);

  consoleLogger.info(`[takeover:${session.id}] Activity tracking started`);
}

/** Stop the always-on activity poll. */
export function stopActivityTracking(session: TakeoverSession): void {
  if (session.activityPollTimer) {
    clearInterval(session.activityPollTimer);
    session.activityPollTimer = null;
  }
}

/**
 * Force-resume the agent: clear the last-input clock and any buffered user
 * events. Used by `take-control` as a hard escape hatch.
 */
export function clearUserActivity(session: TakeoverSession): void {
  session.lastUserInputAt = null;
  session.pendingUserEvents = [];
}

/** Clean up the activity poll timer on session delete. */
export function cleanupActivityTimer(session: TakeoverSession): void {
  stopActivityTracking(session);
}

/* ── Capture script injection ──────────────────────────────────────── */

/**
 * Inject the interaction capture script into a headed session's page.
 * Uses addInitScript so it survives navigations.
 */
export async function injectCaptureScript(client: PlaywrightClient): Promise<void> {
  const page = await client.getPageForReplay();
  if (!page) return;
  try {
    await page.addInitScript(INTERACTION_CAPTURE_SCRIPT);
    await page.evaluate(INTERACTION_CAPTURE_SCRIPT);
  } catch (err) {
    consoleLogger.warn('[capture] Failed to inject interaction capture script', {
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

/* ── Summary formatting ────────────────────────────────────────────── */

/** Format user actions into a human-readable summary for the agent. */
export function formatUserActionsSummary(
  events: TakeoverEvent[],
  snapshot?: string | null
): string {
  const lines: string[] = ['[User Actions]'];
  lines.push('The user performed the following actions since your last tool call:');

  let actionNum = 0;
  for (const evt of events) {
    switch (evt.type) {
      case 'click': {
        actionNum++;
        const info = evt.elementInfo as
          | { ariaLabel?: string; textContent?: string }
          | undefined;
        const target = evt.ariaLabel
          ? `"${evt.ariaLabel}"`
          : evt.textContent
            ? `"${evt.textContent.slice(0, 60)}"`
            : info?.ariaLabel
              ? `"${info.ariaLabel}"`
              : info?.textContent
                ? `"${String(info.textContent).slice(0, 60)}"`
                : evt.testId
                  ? `[data-testid="${evt.testId}"]`
                  : evt.tagName;
        lines.push(`${actionNum}. Clicked ${target} (${evt.tagName})`);
        break;
      }
      case 'input': {
        if (!evt.isFinal) continue;
        actionNum++;
        const field = evt.ariaLabel ? `"${evt.ariaLabel}"` : evt.id ? `#${evt.id}` : evt.tagName;
        const displayValue = evt.inputType === 'password' ? '***' : `"${evt.value}"`;
        lines.push(`${actionNum}. Typed ${displayValue} in ${field}`);
        break;
      }
      case 'change': {
        actionNum++;
        const changeTarget = evt.ariaLabel ?? evt.id ?? evt.tagName;
        lines.push(`${actionNum}. Changed ${changeTarget} to "${evt.value}"`);
        break;
      }
      case 'keydown': {
        if (evt.key === 'Enter' || evt.key === 'Escape') {
          actionNum++;
          lines.push(`${actionNum}. Pressed ${evt.key}`);
        }
        break;
      }
      case 'navigation': {
        actionNum++;
        lines.push(`${actionNum}. Navigated to ${evt.url}`);
        break;
      }
    }
  }

  if (actionNum === 0) return '';

  if (snapshot) {
    lines.push('');
    lines.push('Current page state after user actions:');
    lines.push(snapshot);
  }

  lines.push('---');
  return lines.join('\n');
}
